'use client'

import { useEffect, useState } from 'react'

// Module-level cache: one fetch per category per page load. Without this,
// every mounted component instance fired its own request — the homepage
// gallery alone produced ~100 duplicate /api/site-settings hits
// (2 per ArtworkCard), which saturates mobile connections.
const settingsCache = new Map()

/**
 * Fetch site_settings for a category and normalize to { key: value }.
 * Concurrent/later callers for the same category share one in-flight
 * promise. Failed fetches are evicted so the next mount retries.
 *
 * @param {string|null} category
 * @returns {Promise<Record<string, string>>}
 */
export function fetchSettingsDict(category = null) {
  const key = category || '__all__'
  if (!settingsCache.has(key)) {
    const url = category
      ? `/api/site-settings?category=${encodeURIComponent(category)}`
      : '/api/site-settings'
    const p = fetch(url)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.json()
      })
      .then(arr => {
        const dict = {}
        for (const s of arr) dict[s.key] = s.valueJson ?? s.value ?? ''
        return dict
      })
    p.catch(() => {
      settingsCache.delete(key)
    })
    settingsCache.set(key, p)
  }
  return settingsCache.get(key)
}

/** Test hook: reset the module cache between test cases. */
export function __clearSettingsCache() {
  settingsCache.clear()
}

/**
 * Client hook: fetch site_settings and expose as a dict { key: value }.
 *
 * @param {string|null} category - optional category filter ('brand'|'pricing'|'seo'|null for all)
 * @returns {{ settings: Record<string, string>, loading: boolean, error: string|null }}
 */
export function useSiteSettings(category = null) {
  const [settings, setSettings] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    fetchSettingsDict(category)
      .then(dict => { if (!cancelled) setSettings(dict) })
      .catch(e => { if (!cancelled) setError(e.message) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [category])

  return { settings, loading, error }
}
